import type { SystemStatus } from '@ipfabric/utils/api/systemStatus';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { filesize } from 'filesize';
import { useApi } from 'utils/request/useApi';

type Props = {
  enabled?: boolean;
};

export type ParsedMemory = {
  usedPercent: number | null;
  freeSize: string | null;
  totalSize: string | null;
};
export type ParsedCpu = {
  count: number | null;
  load1m: number | null;
  load5m: number | null;
  load15m: number | null;
  load1mPerCorePercent: number | null;
};
export type ParsedSystemStatus = {
  cpu: ParsedCpu;
  memory: ParsedMemory;
};

export type EnhancedSystemStatus = SystemStatus & {
  parsed: ParsedSystemStatus;
};

const queryParams = { url: '/support/status' };
export const supportStatusQueryKey = [queryParams];

function toPercent(a: number, b: number): number {
  return (100 * a) / b;
}

function parseCpu(data: SystemStatus): ParsedCpu {
  if (!data.cpu) {
    return {
      count: null,
      load1m: null,
      load5m: null,
      load15m: null,
      load1mPerCorePercent: null,
    };
  }
  const count = validNumberOrNull(data.cpu.count);
  const load1m = validNumberOrNull(data.cpu.load1m);
  return {
    count,
    load1m,
    load5m: validNumberOrNull(data.cpu.load5m),
    load15m: validNumberOrNull(data.cpu.load15m),
    load1mPerCorePercent:
      load1m !== null && count !== null && count > 0
        ? toPercent(load1m, count)
        : null,
  };
}

function validNumberOrNull(val: number | null): number | null {
  return Number.isNaN(val) || val === null ? null : val;
}

function parseMemory(data: SystemStatus): ParsedMemory {
  const result: ParsedMemory = {
    usedPercent: null,
    freeSize: null,
    totalSize: null,
  };
  if (!data.memory) {
    return result;
  }
  const free = validNumberOrNull(data.memory.free);
  const used = validNumberOrNull(data.memory.used);
  const total = validNumberOrNull(data.memory.total);
  if (used !== null && total !== null) {
    result.usedPercent = Math.round(toPercent(used, total));
  }
  const filesizeOptions = { standard: 'jedec', output: 'string' } as const;
  if (free !== null) {
    result.freeSize = filesize(free, filesizeOptions);
  }
  if (total !== null) {
    result.totalSize = filesize(total, filesizeOptions);
  }
  return result;
}

export function parseSystemStatus(data: SystemStatus): EnhancedSystemStatus {
  return {
    ...data,
    parsed: {
      cpu: parseCpu(data),
      memory: parseMemory(data),
    },
  };
}

export const useSupportStatusQuery = (
  props?: Props,
): UseQueryResult<EnhancedSystemStatus> => {
  const api = useApi();
  return useQuery({
    ...api.query<SystemStatus>(queryParams),
    enabled: props?.enabled,
    select: (data) => parseSystemStatus(data),
  });
};
